import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { appConfig } from "../config/appConfig";
import { api, setSessionId, getSessionId, takePendingEdition } from "../services";
import { useReducedMotion } from "../hooks/useReducedMotion";
import PlayScreen from "./PlayScreen";
import FaceoffScreen from "./FaceoffScreen";
import TasteReveal from "./TasteReveal";
import BootOverlay from "./BootOverlay";
import TasteProfileView from "./TasteProfileView";
import ProfileCarousel from "./ProfileCarousel";
import { resolveSelectedScope } from "./profileScope";
import TasteMatch from "./TasteMatch";
import SignalCard from "./SignalCard";
import CompareFriendModal from "./CompareFriendModal";
import DevToolbar from "./DevToolbar";
import TestSelector from "./TestSelector";
import Wordmark from "./Wordmark";
import KeepersView from "./KeepersView";
import { canShowProfileNav, canShowKeepersNav } from "./navGating";
import SaveTasteSheet, { startEmergentLogin } from "./SaveTasteSheet";
import EditionsView from "./EditionsView";
import EditionGateScreen from "./EditionGateScreen";
import EditionsUnlockedBanner from "./EditionsUnlockedBanner";
import GuessMyTasteIntro from "./GuessMyTasteIntro";
import GuessMyTasteScreen from "./GuessMyTasteScreen";
import GuessMyTasteResult from "./GuessMyTasteResult";
import { PrivacyPage, TermsPage } from "./LegalPages";

const S = {
  LOADING: "loading", ERROR: "error", HOME: "home", CHALLENGE_INTRO: "challenge_intro", FACEOFF: "faceoff",
  PLAY: "play", REVEAL: "reveal", PROFILE: "profile", MATCH: "match", SIGNAL_CARD: "signalcard", KEEPERS: "keepers",
  EDITIONS: "editions", EDITION_GATE: "edition_gate",
  GUESS_INTRO: "guess_intro", GUESS_PLAY: "guess_play", GUESS_RESULT: "guess_result",
};

function params() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  return { layout: p.get("layout"), stage: p.get("stage"), path: p.get("path"),
           a: p.get("a"), b: p.get("b"),
           challenge: p.get("challenge"), faceoff: p.get("faceoff"), page: p.get("page"),
           edition: p.get("edition"), guess: p.get("guess") };
}

export default function TasteTestApp() {
  const reducedMotion = useReducedMotion();
  const q = params();
  const [stage, setStage] = useState(S.LOADING);
  // Branded boot cover — shown once, only for the app's genuine first S.LOADING
  // (see BootOverlay). Every later S.LOADING (starting a Test, Editions, etc.)
  // falls through to the existing plain inline spinner below, unaffected.
  const [bootOverlayDone, setBootOverlayDone] = useState(false);
  const finishBootOverlay = useCallback(() => setBootOverlayDone(true), []);
  const [variant, setVariant] = useState(q.layout === "showdown" ? "showdown" : q.layout === "duel" ? "duel" : appConfig.layoutVariant);
  const [runId, setRunId] = useState(null);
  const [firstComparison, setFirstComparison] = useState(null);
  const [totalRounds, setTotalRounds] = useState(appConfig.totalRounds);
  const [profile, setProfile] = useState(null);
  // PROFILE = every eligible Taste Signal card the session's accumulated
  // evidence currently supports (FILM/SERIES/GAMES/SCREEN/CROSS-MEDIA), not
  // just the last Test played. `selectedScope` persists across leaving and
  // returning to Profile within this session (see applyProfileScopes below);
  // `profile` always mirrors whichever scope is currently selected so the
  // rest of the app (share card, compare-with-a-friend, keepers logging)
  // keeps working unchanged.
  const [profileScopes, setProfileScopes] = useState(null);
  const [selectedScope, setSelectedScope] = useState(null);
  const [challengerRunId, setChallengerRunId] = useState(null);
  const [challengerHandle, setChallengerHandle] = useState(null);
  const [challengeCtx, setChallengeCtx] = useState(null);
  const [faceoffData, setFaceoffData] = useState(null);
  const [matchData, setMatchData] = useState(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [modalSource, setModalSource] = useState("profile");
  const [legal, setLegal] = useState(q.page === "privacy" || q.page === "terms" ? q.page : null);
  const [errorKind, setErrorKind] = useState(null);
  const [keepers, setKeepers] = useState(null);
  const [keepersLens, setKeepersLens] = useState(null);
  // Returning-user PROFILE nav gating: only a FINALIZED (completed) Profile is
  // reachable via GET /sessions/{id}/profile (404 otherwise). Keepers evidence
  // alone must NOT surface a Profile link that would 404 and bounce the user
  // home. We probe once on HOME and cache the result so the nav is truthful and
  // the click is instant (no re-fetch, no 404 fallback). null = no finalized
  // profile for this session yet.
  const [returningProfile, setReturningProfile] = useState(null);
  const [authUser, setAuthUser] = useState(null);
  const [saveOpen, setSaveOpen] = useState(false);
  const [editions, setEditions] = useState([]);
  const [editionMeta, setEditionMeta] = useState(null);
  const [pendingEditionDetail, setPendingEditionDetail] = useState(null);
  const [editionGateErrored, setEditionGateErrored] = useState(false);
  const [showEditionsUnlocked, setShowEditionsUnlocked] = useState(false);
  const [guessIntro, setGuessIntro] = useState(null); // {sessionId, targetHandle, totalRounds}
  const [guessIntroErrored, setGuessIntroErrored] = useState(false);
  const [guessPlay, setGuessPlay] = useState(null); // {sessionId, targetHandle, totalRounds, firstRound}
  const [guessResult, setGuessResult] = useState(null); // {sessionId, targetHandle, correctCount, totalRounds}
  const bootRef = useRef(false);
  const playedRef = useRef(false);

  // Apply a fetched { scopes, defaultScope } response: keeps a still-eligible
  // current selection (manual or previous) sticky, otherwise falls back to
  // `preferredOverride` (e.g. the Test just completed) or the server's own
  // "most recently played eligible scope" default. Always keeps `profile`
  // (read by REVEAL/SIGNAL_CARD/CompareFriendModal/keepers-logging) in sync
  // with whichever card ends up selected.
  const applyProfileScopes = useCallback((resp, preferredOverride) => {
    const scopes = resp?.scopes || [];
    setProfileScopes(scopes);
    setSelectedScope((prev) => {
      const next = resolveSelectedScope(scopes, prev, preferredOverride || resp?.defaultScope);
      const found = scopes.find((s) => s.scope === next);
      if (found) setProfile(found);
      return next;
    });
    return scopes;
  }, []);

  const selectProfileScope = useCallback((scope) => {
    setSelectedScope(scope);
    setProfileScopes((scopes) => {
      const found = (scopes || []).find((s) => s.scope === scope);
      if (found) setProfile(found);
      return scopes;
    });
  }, []);

  const startNewRun = useCallback(async (testType) => {
    setStage(S.LOADING); setProfile(null); setChallengerRunId(null); setMatchData(null); setEditionMeta(null);
    api.logEvent("territory_selected", null, { testType: testType || "CROSS-MEDIA" });
    if (playedRef.current) api.logEvent("second_test_started", null, { testType: testType || "CROSS-MEDIA" });
    try {
      const r = await api.startRun({ layout: variant, testType });
      setRunId(r.run_id); setFirstComparison(r.comparison); setTotalRounds(r.total_rounds); setStage(S.PLAY);
      api.logEvent("taste_test_started", r.run_id, { testType: testType || "CROSS-MEDIA", source: "organic" });
    } catch { setStage(S.ERROR); }
  }, [variant]);

  // TASTE EDITIONS: same KEEP ONE engine, an edition_id-scoped duel pool, and
  // authenticated-only server-side (see start_run). A 401 here means the
  // caller isn't (or is no longer) signed in — route to the same gate an
  // anonymous deep-link recipient sees, rather than the generic error state.
  const startEditionRun = useCallback(async (edition) => {
    setStage(S.LOADING); setProfile(null); setChallengerRunId(null); setMatchData(null);
    try {
      const r = await api.startRun({ layout: variant, editionId: edition.id });
      setEditionMeta({ id: edition.id, title: edition.title });
      setRunId(r.run_id); setFirstComparison(r.comparison); setTotalRounds(r.total_rounds); setStage(S.PLAY);
      api.logEvent("edition_started", r.run_id, { editionId: edition.id });
    } catch (err) {
      if (String(err?.message || "").startsWith("401")) {
        setPendingEditionDetail(edition); setEditionGateErrored(false); setStage(S.EDITION_GATE);
      } else {
        setStage(S.ERROR);
      }
    }
  }, [variant]);

  const selectEdition = useCallback((edition) => {
    api.logEvent("edition_viewed", null, { editionId: edition.id });
    startEditionRun(edition);
  }, [startEditionRun]);

  const openEditions = useCallback(async () => {
    setStage(S.LOADING);
    try {
      const data = await api.listEditions();
      setEditions(data.editions || []);
      setStage(S.EDITIONS);
    } catch {
      setStage(S.HOME);
    }
  }, []);

  // GUESS MY TASTE — guest-first, never auth-gated. enterGuessPlay is the
  // single place that actually mounts the play screen, shared by the
  // synchronous Match-screen path and the async reciprocal-link path.
  const enterGuessPlay = useCallback((sessionId, targetHandle, totalRoundsN, round, correctSoFar = 0) => {
    setGuessPlay({ sessionId, targetHandle, totalRounds: totalRoundsN, firstRound: round, initialCorrectCount: correctSoFar });
    setStage(S.GUESS_PLAY);
  }, []);

  const startGuessFromMatch = useCallback(async () => {
    setStage(S.LOADING);
    try {
      const r = await api.startGuess(runId, challengerRunId);
      enterGuessPlay(r.guess_session_id, r.target_handle, r.total_rounds, r.round);
    } catch { setStage(S.ERROR); }
  }, [runId, challengerRunId, enterGuessPlay]);

  const beginGuessFromIntro = useCallback(async () => {
    if (!guessIntro) return;
    setStage(S.LOADING);
    try {
      const r = await api.startGuessSession(guessIntro.sessionId);
      if (r.done) {
        setGuessResult({ sessionId: r.guess_session_id, targetHandle: r.target_handle,
          correctCount: r.correct_count, totalRounds: r.total_rounds });
        setStage(S.GUESS_RESULT);
        return;
      }
      // resume mid-session (refresh/reopen): preserves round + score, never rerolls (GATE 8).
      enterGuessPlay(r.guess_session_id, r.target_handle, r.total_rounds, r.round, r.correct_count);
    } catch { setStage(S.ERROR); }
  }, [guessIntro, enterGuessPlay]);

  const handleGuessComplete = useCallback(({ correctCount, totalRounds: finalTotal }) => {
    setGuessResult({ sessionId: guessPlay?.sessionId, targetHandle: guessPlay?.targetHandle,
      correctCount, totalRounds: finalTotal });
    setGuessPlay(null);
    setStage(S.GUESS_RESULT);
  }, [guessPlay]);

  const guessResultDone = useCallback(() => {
    setGuessResult(null);
    setStage(S.HOME);
  }, []);

  useEffect(() => {
    if (bootRef.current) return;
    bootRef.current = true;
    if (legal) return; // legal page short-circuits the normal boot
    (async () => {
      // OAuth callback: Emergent returns to origin with #session_id=... — process
      // it FIRST (link the current anonymous session to the durable user), then
      // strip the fragment and restore the user's living Profile.
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
        const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
        let authedUser = null;
        try {
          const res = await api.authSession(sid);
          authedUser = res.user;
          setAuthUser(authedUser);
        } catch { /* fall through to anonymous */ }
        window.history.replaceState(null, "", window.location.pathname + window.location.search);

        // A pending Edition (set by the anonymous deep-link gate right before
        // this redirect) takes priority over the normal Profile landing —
        // "successful auth returns DIRECTLY to requested Edition."
        const pendingEditionId = authedUser ? takePendingEdition() : null;
        if (pendingEditionId) {
          try {
            const ed = await api.getEdition(pendingEditionId);
            api.logEvent("edition_auth_unlock_completed", null, { editionId: pendingEditionId });
            await startEditionRun(ed);
          } catch { setStage(S.HOME); }
          return;
        }

        try {
          const resp = await api.getProfileScopes();
          applyProfileScopes(resp);
          setStage(S.PROFILE);
          // Organic SAVE YOUR TASTE path: the restrained post-value discovery
          // moment, not shown to anyone arriving via an Edition link already.
          if (authedUser) setShowEditionsUnlocked(true);
        } catch { setStage(S.HOME); }
        return;
      }
      let currentUser = null;
      try { currentUser = await api.authMe(); setAuthUser(currentUser); } catch { /* anonymous */ }
      try {
        if (q.edition) {
          try {
            const ed = await api.getEdition(q.edition);
            api.logEvent("edition_deeplink_opened", null, { editionId: q.edition });
            if (currentUser) {
              await startEditionRun(ed);
            } else {
              setPendingEditionDetail(ed); setEditionGateErrored(false); setStage(S.EDITION_GATE);
            }
          } catch {
            setEditionGateErrored(true); setStage(S.EDITION_GATE);
          }
        } else if (q.guess) {
          try {
            const preview = await api.getGuessSession(q.guess);
            setGuessIntro({ sessionId: q.guess, targetHandle: preview.target_handle, totalRounds: preview.total_rounds });
            setGuessIntroErrored(false);
          } catch {
            setGuessIntroErrored(true);
          }
          setStage(S.GUESS_INTRO);
        } else if (q.challenge) {
          const ctx = await api.getChallenge(q.challenge);
          setChallengeCtx(ctx); setChallengerHandle(ctx.challenger_handle); setStage(S.CHALLENGE_INTRO);
          api.logEvent("challenge_opened", null, { challengeId: q.challenge });
        } else if (q.faceoff) {
          const fo = await api.getFaceoff(q.faceoff);
          setFaceoffData({ shareId: q.faceoff, comparison: fo.comparison, context: fo.context }); setStage(S.FACEOFF);
          api.logEvent("faceoff_opened", null, { shareId: q.faceoff });
        } else if (q.stage === "signalcard") {
          const sim = await api.simulate(q.path || "cerebral");
          setProfile(sim.consumer); setStage(S.SIGNAL_CARD);
        } else if (q.stage === "keepers") {
          const data = await api.getKeepers(getSessionId());
          setKeepers(data); setStage(S.KEEPERS);
        } else if (q.stage === "match") {
          const m = await api.simulateMatch(q.a || "cerebral", q.b || "spectacle");
          setMatchData(m); setChallengerHandle(q.b || "Them"); setStage(S.MATCH);
        } else if (q.stage === "profile" || q.stage === "reveal") {
          const sim = await api.simulate(q.path || "cerebral");
          setProfile(sim.consumer); setStage(q.stage === "reveal" ? S.REVEAL : S.PROFILE);
        } else {
          setStage(S.HOME);
          api.logEvent("landing_view", null, {});
        }
      } catch {
        if (q.challenge || q.faceoff) setErrorKind("link");
        setStage(S.ERROR);
      }
    })();
  }, []);

  const beginChallenge = useCallback(async () => {
    setStage(S.LOADING);
    api.logEvent("challenge_started", null, { challengeId: q.challenge });
    try {
      const r = await api.acceptChallenge(q.challenge, variant);
      setSessionId(r.session_id);
      setRunId(r.run_id); setFirstComparison(r.comparison); setTotalRounds(r.total_rounds);
      setChallengerRunId(r.challenger_run_id); setChallengerHandle(r.challenger_handle); setStage(S.PLAY);
      api.logEvent("taste_test_started", r.run_id, { source: "challenge", challengeId: q.challenge });
    } catch { setStage(S.ERROR); }
  }, [q.challenge, variant]);

  const beginFaceoff = useCallback(async (_side, selectedId) => {
    setStage(S.LOADING);
    api.logEvent("faceoff_choice", null, { shareId: faceoffData?.shareId, selected_id: selectedId });
    try {
      const r = await api.faceoffChoice(faceoffData.shareId, { selected_id: selectedId });
      setSessionId(r.session_id);
      setRunId(r.run_id); setFirstComparison(r.next); setTotalRounds(r.total_rounds); setStage(S.PLAY);
      api.logEvent("taste_test_started", r.run_id, { source: "faceoff", shareId: faceoffData?.shareId });
    } catch { setStage(S.ERROR); }
  }, [faceoffData]);

  const handleComplete = useCallback(async () => {
    try {
      const p = await api.completeRun(runId);
      playedRef.current = true;
      if (editionMeta) {
        api.logEvent("edition_completed", runId, { editionId: editionMeta.id });
        toast("Your taste just got sharper.");
      } else {
        api.logEvent("taste_test_completed", runId, {});
      }
      setProfile(p); setStage(S.REVEAL);
      // Refresh the full accumulated-evidence scope set in the background —
      // may unlock new cards (e.g. this was the Series half of a Screen
      // pair). On this session's very first Test it also becomes the
      // initial default selection; a manually-chosen scope from earlier in
      // the session is preserved (see applyProfileScopes).
      api.getProfileScopes().then((resp) => applyProfileScopes(resp, p.scope)).catch(() => {});
    } catch { setStage(S.ERROR); }
  }, [runId, editionMeta, applyProfileScopes]);

  const revealMatch = useCallback(async () => {
    setStage(S.LOADING);
    try {
      const m = await api.getMatch(runId, challengerRunId);
      setMatchData(m); setStage(S.MATCH);
      api.logEvent("taste_match_viewed", runId, { otherRunId: challengerRunId });
      if (m.sufficient && m.guessMyTasteEnabled) {
        api.logEvent("guess_my_taste_unlock_shown", runId, { otherRunId: challengerRunId });
      }
    } catch { setStage(S.ERROR); }
  }, [runId, challengerRunId]);

  const openCompare = useCallback((src) => {
    setModalSource(src || "profile");
    api.logEvent(src === "match" ? "taste_match_shared" : "next_action", runId, { action: "open_compare" });
    setCompareOpen(true);
  }, [runId]);

  const restart = useCallback(() => {
    setCompareOpen(false); setProfile(null); setChallengerRunId(null); setMatchData(null);
    setErrorKind(null); setLegal(null); setEditionMeta(null); setStage(S.HOME);
  }, []);

  const openSignalCard = useCallback(() => {
    api.logEvent("signal_card_opened", runId, { scope: profile?.scope });
    setStage(S.SIGNAL_CARD);
  }, [runId, profile]);

  const loadKeepers = useCallback(async () => {
    try {
      const data = await api.getKeepers(getSessionId());
      setKeepers(data);
      return data;
    } catch { return null; }
  }, []);

  const openKeepers = useCallback(async (lensKey) => {
    const lk = typeof lensKey === "string" ? lensKey : null;
    api.logEvent("keepers_opened", runId, { scope: profile?.scope, lens: lk });
    if (!keepers) await loadKeepers();
    setKeepersLens(lk);
    setStage(S.KEEPERS);
  }, [keepers, loadKeepers, runId, profile]);

  // Returning-user access from the selector: open the living Profile / Keepers
  // without forcing a fresh Test.
  const openProfileFromSelector = useCallback(async () => {
    // Prefer the scopes already probed on HOME — instant, and can never 404
    // back to the selector. The nav is only shown when this exists (option B).
    if (returningProfile) {
      applyProfileScopes(returningProfile); setStage(S.PROFILE);
      api.logEvent("profile_reopened", null, {});
      return;
    }
    setStage(S.LOADING);
    try {
      const resp = await api.getProfileScopes();
      applyProfileScopes(resp); setStage(S.PROFILE);
      api.logEvent("profile_reopened", null, {});
    } catch { setStage(S.HOME); }
  }, [returningProfile, applyProfileScopes]);

  const openKeepersFromSelector = useCallback(async () => {
    api.logEvent("keepers_opened", null, { source: "selector" });
    const data = keepers || (await loadKeepers());
    setKeepersLens(null);
    if (data) setStage(S.KEEPERS);
  }, [keepers, loadKeepers]);

  const openLegal = useCallback((page) => {
    api.logEvent("legal_viewed", null, { page });
    setLegal(page);
  }, []);

  const dismissEditionsUnlocked = useCallback(() => setShowEditionsUnlocked(false), []);
  const exploreEditionsFromBanner = useCallback(() => {
    setShowEditionsUnlocked(false);
    openEditions();
  }, [openEditions]);

  useEffect(() => {
    if (stage === S.PROFILE && profile) {
      api.logEvent("profile_viewed", runId, { scope: profile?.scope });
      if (!challengerRunId) api.logEvent("guess_my_taste_locked_signal_shown", runId, {});
      loadKeepers();
      // Value-first identity prompt: offer ONCE per session, only after payoff,
      // never to an already-signed-in user, never re-nagging after dismissal.
      const dismissed = typeof window !== "undefined" && sessionStorage.getItem("tt_save_dismissed") === "1";
      if (!authUser && !dismissed) {
        const t = setTimeout(() => setSaveOpen(true), 1400);
        return () => clearTimeout(t);
      }
    }
    if (stage === S.HOME && !keepers) loadKeepers();
    if (stage === S.HOME) {
      // Probe for eligible Profile scopes so the returning-user PROFILE nav
      // only appears when at least one truly exists (option B fix). Never
      // routes to a 404.
      api.getProfileScopes()
        .then((resp) => setReturningProfile(resp))
        .catch(() => setReturningProfile(null));
    }
  }, [stage]);

  const dismissSave = useCallback(() => {
    if (typeof window !== "undefined") sessionStorage.setItem("tt_save_dismissed", "1");
    setSaveOpen(false);
  }, []);

  const logout = useCallback(async () => {
    try { await api.logout(); } catch { /* ignore */ }
    setAuthUser(null);
  }, []);

  // Returning-user LOG IN: reuse the exact same Emergent Managed Auth flow as
  // SAVE YOUR TASTE (provider upserts by email → existing account restored).
  // Guest-first is untouched; this is only an explicit entry point.
  const logIn = useCallback(() => {
    try { api.logEvent("login_started", null, { from: "home" }); } catch { /* ignore */ }
    startEmergentLogin();
  }, []);

  if (legal === "privacy") return <PrivacyPage onBack={() => { setLegal(null); if (stage === S.LOADING) setStage(S.HOME); }} />;
  if (legal === "terms") return <TermsPage onBack={() => { setLegal(null); if (stage === S.LOADING) setStage(S.HOME); }} />;

  return (
    <div className="relative min-h-[100dvh] w-full bg-canvas text-bone overflow-hidden">
      <div className="tt-noise-fixed" aria-hidden />

      {!bootOverlayDone && (
        <BootOverlay active={stage === S.LOADING} onDone={finishBootOverlay} reducedMotion={reducedMotion} />
      )}

      {stage === S.LOADING && (
        <div className="flex items-center justify-center h-[100dvh]">
          <Wordmark variant="horizontal" className="h-4 tt-breathe" />
        </div>
      )}

      {stage === S.HOME && (
        <TestSelector
          onSelect={startNewRun}
          onLegal={openLegal}
          hasEvidence={canShowProfileNav(returningProfile) || canShowKeepersNav(keepers)}
          onProfile={canShowProfileNav(returningProfile) ? openProfileFromSelector : undefined}
          onKeepers={canShowKeepersNav(keepers) ? openKeepersFromSelector : undefined}
          onEditions={authUser ? openEditions : undefined}
          onLogIn={authUser ? undefined : logIn}
        />
      )}

      {stage === S.EDITIONS && (
        <EditionsView editions={editions} loading={false} onBack={() => setStage(S.HOME)} onSelect={selectEdition} />
      )}

      {stage === S.EDITION_GATE && (
        <EditionGateScreen
          edition={pendingEditionDetail}
          errored={editionGateErrored}
          onRetryHome={() => { setEditionGateErrored(false); setPendingEditionDetail(null); setStage(S.HOME); }}
        />
      )}

      {stage === S.ERROR && (
        <div className="flex flex-col items-center justify-center h-[100dvh] gap-4 px-8 text-center max-w-[420px] mx-auto">
          <span className="font-display font-bold uppercase tracking-[0.2em] text-sm text-bone">
            {errorKind === "link" ? "This link isn't available" : "Something interrupted the run"}
          </span>
          <p className="font-body text-[13px] text-silver leading-relaxed">
            {errorKind === "link"
              ? "The shared link may have expired or been mistyped. You can still start your own Taste Test."
              : "No choices were lost — you can pick up a fresh run."}
          </p>
          <button data-testid="error-retry" onClick={() => { setErrorKind(null); setStage(S.HOME); }}
            className="font-body text-xs tracking-[0.18em] uppercase text-white/60 border border-white/15 rounded-full px-5 py-2.5 hover:text-white">Start fresh</button>
        </div>
      )}

      {stage === S.CHALLENGE_INTRO && (
        <div className="flex flex-col items-center justify-center h-[100dvh] gap-6 px-8 text-center max-w-[520px] mx-auto" data-testid="challenge-intro">
          <Wordmark variant="horizontal" className="h-4" />
          <div className="h-px w-24" style={{ background: "linear-gradient(90deg,var(--tt-left),var(--tt-right))" }} />
          <h1 className="font-display font-black uppercase leading-[1.02] tracking-[-0.02em] text-bone text-4xl sm:text-5xl">
            {challengeCtx?.challenger_handle || "A friend"}<br />challenged<br />your taste.
          </h1>
          <p className="font-body text-sm text-silver max-w-xs">24 face-offs. Keep one each time. Then see how close your taste really is.</p>
          <button data-testid="challenge-begin" onClick={beginChallenge}
            className="mt-2 rounded-full px-10 py-4 font-display font-black uppercase tracking-[0.06em] text-sm text-[#05070a]"
            style={{ background: "linear-gradient(90deg,var(--tt-left),var(--tt-right))" }}>Accept challenge</button>
        </div>
      )}

      {stage === S.FACEOFF && faceoffData && (
        <FaceoffScreen comparison={faceoffData.comparison} context={faceoffData.context}
          variant={variant} reducedMotion={reducedMotion} onChoose={beginFaceoff} />
      )}

      {stage === S.PLAY && firstComparison && (
        <PlayScreen runId={runId} firstComparison={firstComparison} total={totalRounds}
          variant={variant} reducedMotion={reducedMotion} onComplete={handleComplete} />
      )}

      {stage === S.REVEAL && (
        <TasteReveal reducedMotion={reducedMotion} onDone={() => setStage(S.PROFILE)} />
      )}

      {stage === S.PROFILE && profile && showEditionsUnlocked && (
        <EditionsUnlockedBanner onExplore={exploreEditionsFromBanner} onDismiss={dismissEditionsUnlocked} />
      )}

      {stage === S.PROFILE && profile && profileScopes && profileScopes.length > 0 && (
        <ProfileCarousel
          scopes={profileScopes}
          selectedScope={selectedScope}
          onSelectScope={selectProfileScope}
          keepers={keepers}
          onSeeKeepers={openKeepers}
          authUser={authUser}
          onSaveTaste={() => setSaveOpen(true)}
          onLogout={logout}
          primaryLabel={challengerRunId ? "See your Taste Match" : "Compare with a friend"}
          onCompare={challengerRunId ? revealMatch : () => openCompare("profile")}
          onShareCard={openSignalCard}
          onRestart={restart}
          // Motivational unlock: only teased while it is genuinely still
          // locked — once a Match exists the real CTA replaces it.
          showGuessLock={!challengerRunId}
        />
      )}
      {/* Dev-only fallback (e.g. ?stage=profile / ?stage=reveal simulate
          paths, which set `profile` directly without ever fetching real
          accumulated scopes) — same view, no carousel chrome. */}
      {stage === S.PROFILE && profile && !(profileScopes && profileScopes.length > 0) && (
        <TasteProfileView
          profile={profile}
          keepers={keepers}
          onSeeKeepers={openKeepers}
          authUser={authUser}
          onSaveTaste={() => setSaveOpen(true)}
          onLogout={logout}
          primaryLabel={challengerRunId ? "See your Taste Match" : "Compare with a friend"}
          onCompare={challengerRunId ? revealMatch : () => openCompare("profile")}
          onShareCard={openSignalCard}
          onRestart={restart}
          showGuessLock={!challengerRunId}
        />
      )}

      <SaveTasteSheet open={saveOpen} onClose={dismissSave} />

      {stage === S.KEEPERS && (
        <KeepersView data={keepers} initialLensKey={keepersLens} backLabel={profile ? "Profile" : "Back"} onBack={() => setStage(profile ? S.PROFILE : S.HOME)} onPlayMore={restart} />
      )}

      {stage === S.MATCH && (
        <TasteMatch match={matchData} challengerHandle={challengerHandle}
          onNext={() => openCompare("match")} onRestart={restart} onGuessMyTaste={startGuessFromMatch} />
      )}

      {stage === S.GUESS_INTRO && (
        <GuessMyTasteIntro
          targetHandle={guessIntro?.targetHandle}
          totalRounds={guessIntro?.totalRounds}
          errored={guessIntroErrored}
          onBegin={beginGuessFromIntro}
          onRetryHome={() => { setGuessIntroErrored(false); setGuessIntro(null); setStage(S.HOME); }}
        />
      )}

      {stage === S.GUESS_PLAY && guessPlay && (
        <GuessMyTasteScreen
          guessSessionId={guessPlay.sessionId}
          targetHandle={guessPlay.targetHandle}
          totalRounds={guessPlay.totalRounds}
          firstRound={guessPlay.firstRound}
          initialCorrectCount={guessPlay.initialCorrectCount}
          variant={variant}
          reducedMotion={reducedMotion}
          onComplete={handleGuessComplete}
        />
      )}

      {stage === S.GUESS_RESULT && guessResult && (
        <GuessMyTasteResult
          correctCount={guessResult.correctCount}
          totalRounds={guessResult.totalRounds}
          guessSessionId={guessResult.sessionId}
          targetHandle={guessResult.targetHandle}
          onDone={guessResultDone}
        />
      )}

      {stage === S.SIGNAL_CARD && profile && (
        <SignalCard profile={profile} onBack={() => setStage(S.PROFILE)} />
      )}

      <CompareFriendModal open={compareOpen} runId={runId || profile?.run_id} profile={profile}
        reducedMotion={reducedMotion} source={modalSource} onClose={() => setCompareOpen(false)} />

      {appConfig.showDevToolbar && stage !== S.FACEOFF && stage !== S.CHALLENGE_INTRO && stage !== S.HOME && (
        <DevToolbar variant={variant} onVariant={setVariant} onRestart={restart} />
      )}
    </div>
  );
}
